import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { resolveGallerySessionUrl } from '@renderer/utils/galleryUrlUtils'
import { useTranslation } from 'react-i18next'
import { useCountdown } from '@renderer/utils/countdownUtils'
import QRCode from 'qrcode'
import { FaStar } from 'react-icons/fa'
import { IoMail } from 'react-icons/io5'
import Button from '@renderer/components/bahela/Button'
import QuitModal from '@renderer/components/bahela/QuitModal'
import ConsentModal from '@renderer/components/bahela/ConsentModal'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import EmailGalleryModal from '@renderer/components/bahela/EmailGalleryModal'
import UserReprintModal from '@renderer/components/bahela/UserReprintModal'

// Import Bahela assets
import bgLayer from '/assets/bahela/bg-layer.webp'
import bahelaLogo from '/assets/bahela/bahela.webp'

const ResultPage: React.FC = () => {
  const { projectId } = useParams()
  const { i18n, t } = useTranslation()
  const {
    gallery,
    id,
    timeline,
    setSessionState,
    uploadSessionState,
    logActivity,
    userPreferences
  } = useSessionStateStore()
  const revokeGalleryUrls = useSessionStateStore((s) => s.revokeGalleryUrls)
  const cleanupSession = useSessionStateStore((s) => s.cleanupSession)
  const { config: globalConfig } = useConfigStore()

  /** Full page reload to welcome — resets SPA state so the next session (payment → capture) is reliable. */
  const finishSessionAndReloadWelcome = useCallback(async (): Promise<void> => {
    await revokeGalleryUrls()
    await cleanupSession()
    const base = projectId ? `/${projectId}` : '/'
    window.location.assign(base)
  }, [cleanupSession, projectId, revokeGalleryUrls])
  const config = globalConfig?.pages.result
  const [remainingTime, setRemainingTime] = useState<number>(120)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [qrCodeImage, setQrCodeImage] = useState<string>('')
  const [stripUrl, setStripUrl] = useState<string>('')
  const [satisfactionRating, setSatisfactionRating] = useState<number | null>(
    userPreferences.satisfactionRating || null
  )
  const [hoveredStar, setHoveredStar] = useState<number | null>(null)
  const [isTouchDevice, setIsTouchDevice] = useState<boolean>(false)

  // Check if askUserConsent is configured, default to true if not found
  const askUserConsent =
    config?.settings?.askUserConsent === undefined ? true : config?.settings?.askUserConsent

  const [showConsent, setShowConsent] = useState<boolean>(askUserConsent)
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false)
  const [showReprint, setShowReprint] = useState<boolean>(false)

  // Live/story video output (when user chose recording)
  const recordingType = userPreferences.recordingType
  const showVideoOutput =
    userPreferences.useRecording &&
    (recordingType === 'strip_live' || recordingType === 'strip_story')
  const galleryWithLive = gallery as {
    liveModeLocalUrl?: string
    liveModeUrl?: string | null
    liveModeProcessing?: boolean
  }
  const videoUrl = galleryWithLive.liveModeLocalUrl ?? galleryWithLive.liveModeUrl ?? null
  const liveModeProcessing = galleryWithLive.liveModeProcessing ?? false

  // Initialize countdown hook
  const resultCountdown = useCountdown(config?.settings.resultTimeout || 120)

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  // Handle consent response
  const handleConsent = async (consent: boolean): Promise<void> => {
    const reprintEnabled = config?.settings?.reprintEnabled ?? false

    // Transition the UI in a single render batch: hide consent and (when
    // enabled) show the reprint modal together, so the result/QR page never
    // flashes between the two modals. The consent upload below now runs in the
    // background and no longer gates this transition. (Matches view/Result.)
    if (reprintEnabled) {
      setShowReprint(true)
    } else {
      resultCountdown.start()
    }
    setShowConsent(false)

    // Update session state with consent
    setSessionState({
      userConsent: {
        photoPublication: consent,
        consentTimestamp: new Date()
      }
    })

    // Log consent activity
    logActivity(`User ${consent ? 'granted' : 'declined'} photo publication consent`, {
      consent,
      timestamp: new Date()
    })

    // CRITICAL: Wait for state to propagate, then upload to ensure consent is saved
    await new Promise((resolve) => setTimeout(resolve, 100))
    const currentState = useSessionStateStore.getState()

    // Verify consent was set correctly
    if (currentState.userConsent?.photoPublication !== consent) {
      console.warn('[CONSENT] Consent value mismatch, retrying state update')
      setSessionState({
        userConsent: {
          photoPublication: consent,
          consentTimestamp: new Date()
        }
      })
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    // Upload session state with consent (background — UI already transitioned)
    await uploadSessionState(id)
    console.log('[CONSENT] Uploaded consent:', consent)
  }

  // Handle satisfaction rating
  const handleRating = (rating: number): void => {
    // Clear hover state immediately on touch devices to prevent visual issues
    if (isTouchDevice) {
      setHoveredStar(null)
    }

    // If clicking the same star, deselect it
    if (satisfactionRating === rating) {
      setSatisfactionRating(null)
      setSessionState({
        userPreferences: {
          satisfactionRating: null,
          satisfactionTimestamp: null
        }
      })
      logActivity(`User deselected satisfaction rating`)
    } else {
      setSatisfactionRating(rating)
      const timestamp = new Date()
      setSessionState({
        userPreferences: {
          satisfactionRating: rating,
          satisfactionTimestamp: timestamp
        }
      })
      logActivity(`User rated session satisfaction: ${rating} stars`, {
        rating,
        timestamp
      })
    }
  }

  useEffect((): (() => void) => {
    // Detect touch device
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0)

    // Find and set strip URL first to ensure it's available throughout the component lifecycle
    const stripItem = gallery.capturedItems.find((item) => item.stripUrl)
    if (stripItem?.stripUrl) {
      setStripUrl(stripItem.stripUrl)
    }

    resultCountdown.setOnTick(() => {
      setRemainingTime(resultCountdown.getRemainingSeconds())
    })

    resultCountdown.setOnComplete(() => {
      // Log result timeout activity
      logActivity('Result page timed out', {
        type: 'result',
        duration: config?.settings.resultTimeout || 120
      })

      const currentState = useSessionStateStore.getState()

      // Ensure isCompleted is set
      if (currentState.timeline.isCompleted !== true) {
        setSessionState({
          timeline: {
            isCompleted: true
          }
        })
      }

      // Sync satisfaction rating if needed
      if (
        satisfactionRating !== null &&
        currentState.userPreferences.satisfactionRating !== satisfactionRating
      ) {
        setSessionState({
          userPreferences: {
            satisfactionRating: satisfactionRating,
            satisfactionTimestamp: currentState.userPreferences.satisfactionTimestamp || new Date()
          }
        })
      }

      // Snapshot state so upload uses this session's data after cleanup/navigate (avoids uploading under next sessionId)
      const snapshot = useSessionStateStore.getState()
      uploadSessionState(id, snapshot).catch((err) =>
        console.warn('[RESULT] Background upload on timeout failed:', err)
      )

      // Full reload welcome — fresh app state for next customer session
      void finishSessionAndReloadWelcome()
    })

    // Only start countdown if consent is not required and reprint is not shown
    const reprintEnabled = config?.settings?.reprintEnabled ?? false
    if (!askUserConsent && reprintEnabled) {
      setShowReprint(true)
    } else if (!askUserConsent) {
      resultCountdown.start()
    }

    const setupResult = async (): Promise<void> => {
      // Set endTime, duration, and isCompleted together in ONE state update
      // This ensures the hash changes once with all completion data, triggering proper queue
      const endTime = new Date()
      const duration = timeline?.startTime
        ? endTime.getTime() - new Date(timeline.startTime).getTime()
        : null

      setSessionState({
        timeline: {
          endTime: endTime,
          duration: duration,
          isCompleted: true
        }
      })

      // Log session completion activity
      logActivity('Session completed', {
        stripUrl,
        sessionId: id,
        endTime: new Date()
      })

      // Generate QR code
      try {
        const galleryTarget = resolveGallerySessionUrl(id, config?.settings)
        const qrCode = await QRCode.toDataURL(galleryTarget, {
          margin: 0,
          width: 300
        })
        setQrCodeImage(qrCode)
      } catch (error) {
        console.error('Failed to generate QR code:', error)
      }

      // Ensure state update is applied before uploading
      // Wait for Zustand state to propagate (increased wait time for reliability)
      await new Promise((resolve) => setTimeout(resolve, 100))

      // Double-check state before upload to ensure isCompleted is set
      const currentState = useSessionStateStore.getState()
      if (currentState.timeline.isCompleted !== true) {
        console.warn('[RESULT] isCompleted not set in state, retrying state update')
        // Retry state update if it didn't propagate
        setSessionState({
          timeline: {
            isCompleted: true,
            endTime: endTime,
            duration: duration
          }
        })
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      // Only upload if consent is not required OR consent has been given
      // This prevents uploading without consent data when askUserConsent is true
      if (!askUserConsent) {
        console.log('[RESULT] Uploading session with isCompleted:', currentState.timeline.isCompleted)
        // Upload session state with isCompleted flag, endTime, and duration
        await uploadSessionState(id)
      } else {
        console.log('[RESULT] Skipping initial upload - waiting for user consent')
      }
    }

    setupResult()

    return () => {
      resultCountdown.stop()
    }
  }, [finishSessionAndReloadWelcome])

  // Call all hooks at the top level
  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedBahelaLogo = useCachedImage(bahelaLogo)
  const cachedStripUrl = useCachedImage(stripUrl || '')
  const cachedQrCodeImage = useCachedImage(qrCodeImage)

  return (
    <>
      {/* Consent Modal - Only show if askUserConsent is true */}
      {askUserConsent && (
        <ConsentModal
          isVisible={showConsent}
          onConsent={handleConsent}
          stripPreview={stripUrl}
          mode="capture"
        />
      )}

      {/* Reprint Modal - gated by config, shows after consent or immediately if consent disabled */}
      {config?.settings?.reprintEnabled && (
        <UserReprintModal
          isVisible={showReprint}
          onComplete={() => {
            setShowReprint(false)
            resultCountdown.start()
          }}
          onSkip={() => {
            setShowReprint(false)
            resultCountdown.start()
          }}
          stripPreview={stripUrl}
          timeout={config?.settings?.reprintTimeout ?? 60}
        />
      )}

      {/* Main Result Content - Only show after consent and reprint (if enabled) */}
      {!showConsent && !showReprint && (
        <div className="relative bg-methadone w-full h-full overflow-hidden">
          <QuitModal showModal={showModal} setShowModal={setShowModal} />

          {/* Email Gallery Modal - toggled by project config */}
          {(config?.settings?.emailEnabled === undefined ||
            config?.settings?.emailEnabled) &&
            showEmailModal &&
            id && (
              <EmailGalleryModal
                sessionId={id}
                onClose={() => setShowEmailModal(false)}
              />
            )}

          {/* Background Layer */}
          <div
            className="absolute top-0 left-0 w-full h-full bg-cover z-[0]"
            style={{ backgroundImage: `url(${cachedBgLayer})` }}
          ></div>

          {/* Main Content */}
          <div className="absolute top-[192px] left-[208px] flex flex-col text-uriYellow w-[677px]">
            <h1 className="text-[100px] w-[330px] leading-[1.10] font-serif-bahela text-left flex drop-shadow-2xl">
              {i18n.language === 'id' ? 'Fotomu sedang dicetak!' : 'Printing your strips...'}
            </h1>
            <h3 className="text-[55px] w-[400px] mt-[33px] font-light text-left font-body-bahela drop-shadow-2xl">
              {i18n.language === 'id' ? 'Jumpa lagi, Tuan dan Nona' : 'See you next time'}
            </h3>
            <img
              src={cachedBahelaLogo}
              alt="Bahela"
              className="object-contain mt-[105px] w-[127px] h-[114px] drop-shadow-2xl"
            />
          </div>

          {/* Strip Preview */}
          {stripUrl && (
            <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 flex flex-col items-center">
              <img
                className="object-contain drop-shadow-2xl shadow-2xl w-[450px]"
                src={cachedStripUrl}
                alt="Bahela Photo Strip"
              />

              {/* Live/Story video output when user chose recording */}
              {showVideoOutput && (
                <div className="mt-8 flex flex-col items-center gap-4 w-full max-w-[450px]">
                  {liveModeProcessing && (
                    <p className="text-uriYellow text-[28px] font-body-bahela drop-shadow-2xl">
                      {i18n.language === 'id'
                        ? 'Memproses video...'
                        : 'Processing video...'}
                    </p>
                  )}
                  {!liveModeProcessing && videoUrl && (
                    <div className="w-full rounded-lg overflow-hidden drop-shadow-2xl bg-black/20">
                      <video
                        src={videoUrl}
                        controls
                        className="w-full h-auto max-h-[400px] object-contain"
                        playsInline
                      />
                      <p className="text-white text-[22px] font-body-bahela mt-2 text-center">
                        {recordingType === 'strip_story'
                          ? i18n.language === 'id'
                            ? 'Video story'
                            : 'Story video'
                          : i18n.language === 'id'
                            ? 'Video live'
                            : 'Live video'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Satisfaction Rating Section - Under Photo Strip */}
              <div className="flex flex-col items-center mt-8 gap-4">
                <div className="text-center">
                  <h3 className="text-uriYellow text-[35px] font-body-bahela mb-2 drop-shadow-2xl">
                    {i18n.language === 'id'
                      ? 'Bagaimana pengalaman Anda?'
                      : 'How was your experience?'}
                  </h3>
                  <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                      satisfactionRating ? 'h-0 opacity-0' : 'h-auto opacity-100'
                    }`}
                  >
                    <p className="text-white text-[25px] font-body-bahela drop-shadow-lg">
                      {i18n.language === 'id' ? 'Beri kami rating' : 'Rate your experience'}
                    </p>
                  </div>
                </div>

                {/* Star Rating */}
                <div
                  className={`flex gap-2 transition-all duration-500 ease-in-out ${
                    satisfactionRating ? '-mt-4' : 'mt-0'
                  }`}
                >
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => handleRating(star)}
                      onMouseEnter={() => !isTouchDevice && setHoveredStar(star)}
                      onMouseLeave={() => !isTouchDevice && setHoveredStar(null)}
                      onTouchStart={() => {
                        // On touch devices, briefly show hover state for immediate feedback
                        if (isTouchDevice) {
                          setHoveredStar(star)
                          // Clear hover state after a short delay
                          setTimeout(() => setHoveredStar(null), 150)
                        }
                      }}
                      className={`transition-all duration-200 text-4xl p-2 ${
                        isTouchDevice ? 'active:scale-95' : 'hover:scale-110'
                      }`}
                      title={`${star} star${star > 1 ? 's' : ''}`}
                    >
                      <FaStar
                        className={`${
                          (hoveredStar && star <= hoveredStar) ||
                          (satisfactionRating && star <= satisfactionRating)
                            ? 'text-uriYellow'
                            : 'text-white/40'
                        } transition-colors duration-200 drop-shadow-lg`}
                      />
                    </button>
                  ))}
                </div>

                {/* Thank you message */}
                {satisfactionRating && (
                  <div className="text-center animate-fade-in">
                    <p className="text-white text-[28px] font-body-bahela font-medium drop-shadow-2xl">
                      {i18n.language === 'id' ? 'Terima kasih!' : 'Thank you!'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* QR Code Section */}
          <div className="absolute top-[200px] right-[210px] flex flex-col items-center gap-8">
            <p className="text-white text-[30px] font-body-bahela">
              {i18n.language === 'id' ? 'Akses foto digital:' : 'Access to digital photos:'}
            </p>
            <div className="w-[300px] h-[300px] p-6 bg-white flex items-center justify-center">
              {qrCodeImage ? (
                <img
                  className="w-full h-full"
                  crossOrigin="anonymous"
                  src={cachedQrCodeImage}
                  alt="QR Code"
                />
              ) : (
                <svg
                  className="w-24 h-24 animate-spin text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
            </div>
            <span className="text-white text-[30px] font-body-bahela text-center">
              {'https://bahela.id'}
              <br />
              {i18n.language === 'id' ? 'Kode unik : ' : 'Unique code: '}
              {id}
            </span>
          </div>

          {/* Bottom Actions */}
          <div className="absolute z-[6] right-[200px] bottom-[130px] drop-shadow-2xl flex flex-col items-center gap-4">
            <span className="text-[50px] text-white font-serif-bahela">
              {formatTime(remainingTime)}
            </span>
            <div className="flex flex-col gap-4 items-center">
              <Button
                onClick={() => {
                  // Stop countdown to prevent timeout from firing
                  resultCountdown.stop()

                  const currentState = useSessionStateStore.getState()

                  // Ensure isCompleted is set
                  if (currentState.timeline.isCompleted !== true) {
                    setSessionState({
                      timeline: {
                        isCompleted: true
                      }
                    })
                  }

                  // Sync satisfaction rating if needed
                  if (
                    satisfactionRating !== null &&
                    currentState.userPreferences.satisfactionRating !== satisfactionRating
                  ) {
                    setSessionState({
                      userPreferences: {
                        satisfactionRating: satisfactionRating,
                        satisfactionTimestamp:
                          currentState.userPreferences.satisfactionTimestamp || new Date()
                      }
                    })
                  }

                  // Snapshot state so upload uses this session's data after cleanup/navigate (avoids uploading under next sessionId)
                  const snapshot = useSessionStateStore.getState()
                  uploadSessionState(id, snapshot).catch((err) =>
                    console.warn('[RESULT] Background upload failed:', err)
                  )

                  void finishSessionAndReloadWelcome()
                }}
                className="text-[50px]"
                textColor="methadone"
              >
                {i18n.language === 'id' ? 'Beranda' : 'Homepage'}
              </Button>
              {(config?.settings?.emailEnabled === undefined ||
                config?.settings?.emailEnabled) && (
                <Button
                  onClick={() => setShowEmailModal(true)}
                  className="text-[50px]"
                  bgColor="uriYellow"
                  textColor="methadone"
                >
                  <span className="flex items-center gap-2">
                    <IoMail className="text-2xl" />
                    {t('result.emailGallery.sendEmail', 'Send Email')}
                  </span>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ResultPage
